<template>
	<div v-if="walkthroughData" class="walkthrough-carousel-container">
			<!-- Tap zones for story variant (Instagram-style navigation) -->
			<div v-if="variant === 'story'" class="story-tap-zones">
				<div class="tap-zone tap-left" @click="handleTapLeft"></div>
				<div class="tap-zone tap-right" @click="handleTapRight"></div>
			</div>

			<!-- Progress bar for story variant -->
			<div v-if="variant === 'story' && walkthroughData.slides && walkthroughData.slides.length > 1" class="progress-bar-container">
				<div class="progress-bar">
					<div
						v-for="(slide, index) in walkthroughData.slides"
						:key="`progress-${index}`"
						class="progress-segment"
						:class="{
							completed: index < currentSlideIndex || (index === currentSlideIndex && !walkthroughData.autoScrollSeconds),
							animating: index === currentSlideIndex && walkthroughData.autoScrollSeconds > 0
						}"
						:style="index === currentSlideIndex && walkthroughData.autoScrollSeconds > 0 ? { '--animation-duration': walkthroughData.autoScrollSeconds + 's' } : {}"
					></div>
				</div>
			</div>

			<SwipeCarousel
				v-model="currentSlideIndex"
				:showArrows="false"
				:showButtons="false"
				:paginationDots="false"
				:itemCount="walkthroughData.slides.length"
				:height="height"
				@update:model-value="handleSlideChange"
			>
				<v-carousel-item v-for="(slide, index) in walkthroughData.slides" :key="`${slide.id}-${index}`">
					<MGSheetSlide
						:imageUri="slide.imageUri"
						:videoLoop="slide.videoLoop"
						:variant="variant"
						:imageHeight="imageHeight"
						:actionUri="slide.actionUri"
					/>
				</v-carousel-item>
			</SwipeCarousel>

			<!-- Pagination dots above text -->
			<div v-if="dotsAbove" class="pagination-container">
				<PaginationDots
					v-if="variant === 'sheet' && walkthroughData.slides && walkthroughData.slides.length > 1"
					:total-items="walkthroughData.slides.length"
					:current-index="currentSlideIndex"
					:maxDots="5"
				/>
			</div>

			<div class="slide-text-stack" @touchstart="onSwipeStart" @touchend="onSwipeEnd">
				<div
					v-for="(slide, index) in walkthroughData.slides"
					:key="`text-${index}`"
					class="slide-text-content"
					:class="{ 'slide-text-visible': index === currentSlideIndex }"
				>
					<h2 v-if="slide.title" class="slide-title">{{ slide.title }}</h2>
					<!-- <p v-if="slide.actionUri && slide.actionUri.toLowerCase().includes('truelayer')" class="open-banking-label"><img :src="require('@/assets/figureofeight.png')" class="open-banking-icon" /> Connect with <b>OPEN BANKING</b></p> -->
					<p v-if="slide.description" class="slide-description">{{ slide.description }}</p>
					<div v-if="!compact && slide.subDescription" class="slide-sub-description">
						{{ slide.subDescription }}
					</div>
				</div>
			</div>

			<div>
				<div class="small-text-stack">
					<div
						v-for="(slide, index) in walkthroughData.slides"
						:key="`small-${index}`"
						class="smallTextLink"
						:class="{ 'slide-text-visible': index === currentSlideIndex }"
					>
						<a
							v-if="slide.smallText || slide.smallLink"
							:href="slide.smallLink"
							class="slide-small-link"
							target="_blank"
						>
							* {{ slide.smallText || slide.smallLink }}
						</a>
					</div>
				</div>
			</div>

			<div>
				<!-- Pagination dots below text -->
				<div v-if="!dotsAbove" class="pagination-container">
					<PaginationDots
						v-if="variant === 'sheet' && walkthroughData.slides && walkthroughData.slides.length > 1"
						:total-items="walkthroughData.slides.length"
						:current-index="currentSlideIndex"
						:maxDots="5"
					/>
				</div>

				<div class="action-button" :class="{ 'anchor-bottom-no-nav': variant === 'story' }">
					<MGButton
						v-if="currentActionText"
						:text="currentActionText"
						:arrow="true"
						:fullWidth="true"
						:noCaps="true"
						:backgroundColor="currentActionButtonColour"
						@click="handleActionClick"
					/>
				</div>
			</div>
		</div>
</template>

<script>
import MGSheetSlide from './MGSheetSlide.vue';
import PaginationDots from './PaginationDots.vue';
import SwipeCarousel from './SwipeCarousel.vue';
import MGButton from './MGButton.vue';

export default {
	name: 'MGWalkthrough',
	components: { MGSheetSlide, PaginationDots, SwipeCarousel, MGButton },
	props: {
		walkthroughData: {
			type: Object,
			required: true
		},
		variant: {
			type: String,
			default: 'sheet',
			validator: (value) => ['sheet', 'story'].includes(value)
		},
		slideKey: {
			type: String,
			default: null
		},
		height: {
			type: String,
			default: null
		},
		compact : {
			type: Boolean,
			default: false
		},
		imageHeight: {
			type: String,
			default: '220'
		},
		dotsAbove: {
			type: Boolean,
			default: true
		}
	},
	emits: ['slide-title-changed', 'close-drawer', 'slide-index-changed', 'action-clicked'],
	data() {
		return {
			currentSlideIndex: 0,
			autoScrollTimer: null,
			preloadedMedia: [],
			swipeStartX: 0
		}
	},
	computed: {
		currentSlide() {
			if (!this.walkthroughData || !this.walkthroughData.slides || this.walkthroughData.slides.length === 0) {
				return null;
			}
			return this.walkthroughData.slides[this.currentSlideIndex] || null;
		},
		currentSlideTitle() {
			return this.currentSlide?.screenTitle || '';
		},
		currentTitle() {
			return this.currentSlide?.title || '';
		},
		currentDescription() {
			return this.currentSlide?.description || '';
		},
		currentSubDescription() {
			return this.currentSlide?.subDescription || '';
		},
		currentActionText() {
			return this.currentSlide?.actionText || '';
		},
		currentActionUri() {
			return this.currentSlide?.actionUri || '';
		},
		currentSmallText() {
			return this.currentSlide?.smallText || '';
		},
		currentSmallLink() {
			return this.currentSlide?.smallLink || '';
		},
		currentActionButtonColour() {
			return this.currentSlide?.actionButtonColour || null;
		},
		isTruelayer() {
			return this.currentActionUri && this.currentActionUri.toLowerCase().includes('truelayer');
		}
	},
	watch: {
		walkthroughData: {
			handler() {
				if (this.walkthroughData && this.walkthroughData.slides) {
					const title = this.walkthroughData.slides[this.currentSlideIndex]?.screenTitle || '';
					this.$emit('slide-title-changed', title);
					if (this.walkthroughData.autoScrollSeconds > 0) {
						this.startAutoScroll();
					}
					this.preloadAllMedia();
				}
			}
		},
		slideKey: {
			handler(newSlideKey) {
				if (newSlideKey && this.walkthroughData && this.walkthroughData.slides) {
					const matchingSlideIndex = this.walkthroughData.slides.findIndex(
						slide => slide.key === newSlideKey
					);
					if (matchingSlideIndex !== -1) {
						this.currentSlideIndex = matchingSlideIndex;
					}
					else {
						this.currentSlideIndex = 0;
					}
				}
			}
		},
		currentSlideIndex: {
			handler(newIndex) {
				if (this.walkthroughData && this.walkthroughData.slides) {
					const title = this.walkthroughData.slides[newIndex]?.screenTitle || '';
					this.$emit('slide-title-changed', title);
					this.$emit('slide-index-changed', newIndex);
				}
			}
		}
	},
	methods: {
		handleActionClick() {
			this.$emit('action-clicked', this.currentActionUri || null);
			if (this.currentActionUri) {
				return;
			}
			if (this.currentActionText) {
				this.goToNextSlide();
			}
		},
		startAutoScroll() {
			this.stopAutoScroll();
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.autoScrollTimer = setInterval(() => {
					this.goToNextSlide();
				}, this.walkthroughData.autoScrollSeconds * 1000);
			}
		},
		stopAutoScroll() {
			if (this.autoScrollTimer) {
				clearInterval(this.autoScrollTimer);
				this.autoScrollTimer = null;
			}
		},
		goToNextSlide() {
			if (this.walkthroughData && this.walkthroughData.slides) {
				const nextIndex = this.currentSlideIndex + 1;
				if (nextIndex < this.walkthroughData.slides.length) {
					this.currentSlideIndex = nextIndex;
				} else if (this.variant === 'story') {
					this.currentSlideIndex = 0;
				}
			}
		},
		goToPreviousSlide() {
			if (this.currentSlideIndex > 0) {
				this.currentSlideIndex = this.currentSlideIndex - 1;
			}
		},
		handleSlideChange() {
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.startAutoScroll();
			}
		},
		handleTapLeft() {
			this.goToPreviousSlide();
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.startAutoScroll();
			}
		},
		handleTapRight() {
			this.goToNextSlide();
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.startAutoScroll();
			}
		},
		onSwipeStart(e) {
			this.swipeStartX = e.touches[0].clientX;
		},
		onSwipeEnd(e) {
			const diff = this.swipeStartX - e.changedTouches[0].clientX;
			if (Math.abs(diff) > 50) {
				if (diff > 0) {
					this.goToNextSlide();
				} else {
					this.goToPreviousSlide();
				}
			}
		},
		preloadAllMedia() {
			if (!this.walkthroughData || !this.walkthroughData.slides) return;

			const videoExtensions = ['.mp4', '.webm', '.mov', '.avi'];

			this.walkthroughData.slides.forEach(slide => {
				if (!slide.imageUri) return;

				const isVideo = videoExtensions.some(ext =>
					slide.imageUri.toLowerCase().endsWith(ext)
				);

				if (isVideo) {
					const video = document.createElement('video');
					video.preload = 'auto';
					video.src = slide.imageUri;
					this.preloadedMedia.push(video);
				} else {
					const img = new Image();
					img.src = slide.imageUri;
					this.preloadedMedia.push(img);
				}
			});
		}
	},
	beforeUnmount() {
		this.stopAutoScroll();
	},
	mounted() {
		// Emit title on mount in case prop was already loaded
		if (this.walkthroughData && this.walkthroughData.slides) {
			// If slideKey is provided, find and set the matching slide
			if (this.slideKey) {
				const matchingSlideIndex = this.walkthroughData.slides.findIndex(
					slide => slide.key === this.slideKey
				);
				if (matchingSlideIndex !== -1) {
					this.currentSlideIndex = matchingSlideIndex;
				}
			}

			const title = this.walkthroughData.slides[this.currentSlideIndex]?.screenTitle || '';
			this.$emit('slide-title-changed', title);
			if (this.walkthroughData.autoScrollSeconds > 0) {
				this.startAutoScroll();
			}
			this.preloadAllMedia();
		}
	}
}
</script>

<style scoped>
.action-button {
	margin-left: 20px;
	margin-right: 20px;
}
.progress-bar-container {
	width: 100%;
	padding: 12px 12px 8px 12px;
	box-sizing: border-box;
}

.progress-bar {
	width: 100%;
	display: flex;
	gap: 4px;
}

.progress-segment {
	flex: 1;
	height: 4px;
	background-color: rgba(0, 0, 0, 0.1);
	border-radius: 2px;
	position: relative;
	overflow: hidden;
}

.progress-segment.completed {
	background-color: var(--background-color-alt2, #332847);
}

.progress-segment.animating::after {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	background-color: var(--background-color-alt2, #332847);
	border-radius: 2px;
	animation: progressFill var(--animation-duration, 5s) linear forwards;
}

@keyframes progressFill {
	from {
		width: 0%;
	}
	to {
		width: 100%;
	}
}

.pagination-container {
	margin-top: 12px;
	margin-bottom: 8px;
	display: flex;
	justify-content: center;
}

.small-text-stack {
	display: grid;
	width: 100%;
}

.smallTextLink {
	grid-area: 1 / 1;
	margin-bottom: -32px;
	padding-left: 25px;
	visibility: hidden;
}
.walkthrough-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
}

.walkthrough-title {
	font-size: 18px;
	font-weight: bold;
	color: var(--text-color);
	margin: 0;
}

.walkthrough-carousel-container {
	width: 100%;
	position: relative;
}

.story-tap-zones {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 100px;
	z-index: 10;
	display: flex;
}

.tap-zone {
	flex: 1;
	cursor: pointer;
}

.tap-left {
	flex: 0.3;
}

.tap-right {
	flex: 0.7;
}

.walkthrough-carousel-container :deep(.swipe-carousel) {
	padding-bottom: 0;
	margin-bottom: 0;
}

.slide-text-stack {
	display: grid;
	width: 100%;
}

.slide-text-content {
	grid-area: 1 / 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 8px;
	width: 100%;
	visibility: hidden;
}

.slide-text-visible {
	visibility: visible;
}

.slide-title {
	font-size: 20px;
	font-weight: bold;
	color: var(--text-color);
}

.open-banking-label {
	font-size: 11px;
	margin: 0;
	opacity: 0.7;
	margin-top: -17px;
	margin-bottom: -12px;
}

.open-banking-icon {
	height: 8px;
	vertical-align: middle;
	margin-right: 4px;
}

.slide-description {
	font-size: 13px;
	max-width: 100%;
}

.slide-sub-description {
	background: linear-gradient(90deg, #E1C6C8 0%, #FCE5D4 100%);
	border-radius: 8px;
	font-size: 13px;
	color: var(--text-color);
	padding: 4px;
	gap: 8px;
}

/* Add extra spacing on smaller screens when subdescription wraps */
@media (max-height: 700px) {
	.pagination-container {
		margin-top: 24px !important;
		margin-bottom: 8px;
	}

	.smallTextLink {
		margin-top: 8px;
	}
}
</style>
